/**
 * Return a multi-step form to the top of the page, reliably, on iOS.
 *
 * A bare `window.scrollTo(0, 0)` looks correct and fails on device. Two things
 * defeat it:
 *
 *  1. Layout timing. Called straight from a step-change effect, the incoming
 *     step's layout may not be committed yet, so the scroll is computed against
 *     the outgoing height. The double requestAnimationFrame waits for paint.
 *
 *  2. The iOS keyboard. Multi-step forms are mostly text fields, so the keyboard
 *     is usually up when the user taps Continue. Blurring the field dismisses it,
 *     but the WebView restores its own scroll offset when the dismissal animation
 *     finishes — roughly 300ms later, well after our scroll ran. iOS wins and the
 *     new step opens mid-page. Re-asserting on a short schedule, plus once more
 *     when visualViewport resizes (which is the keyboard actually leaving),
 *     outlasts it. Timers rather than @capacitor/keyboard because that plugin
 *     isn't a dependency here.
 *
 * The retry window is deliberately under a second so it can never fight a user
 * who scrolls down on purpose.
 *
 * @param {{ blur?: boolean }} [opts] blur the focused element first, which is
 *   what dismisses the keyboard and stops the browser scrolling the focused
 *   Continue button back into view at the bottom. Default true.
 * @returns {() => void} cleanup — cancels pending retries and detaches the
 *   listener. Call it on unmount, and before starting a new scroll.
 */
export function scrollPageToTop({ blur = true } = {}) {
  return reassertScroll(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    // Some mobile browsers scroll the document element or body rather than the
    // window, so set all three.
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, { blur });
}

/**
 * Bring one element into view, with the same iOS durability as
 * scrollPageToTop. Use this for messages the user has to read after tapping a
 * control that sits far from them — a validation error at the top of a long
 * form, for example, where a bare scrollIntoView gets undone by the WebView and
 * the form looks like it did nothing at all.
 *
 * Takes a getter rather than a node so the target can be resolved on each retry;
 * the element often does not exist yet when the scroll is requested (it renders
 * in the same commit as the state change that triggered this).
 *
 * @param {() => (Element | null | undefined)} getElement resolves the target.
 * @param {{ blur?: boolean, block?: ScrollLogicalPosition }} [opts] `blur` as in
 *   scrollPageToTop. `block` is scrollIntoView's own alignment: 'center' suits a
 *   short message, while anything taller than the viewport wants 'start', since
 *   centering a tall panel pushes its opening line off the top of the screen.
 * @returns {() => void} cleanup, same contract as scrollPageToTop.
 */
export function scrollElementIntoView(getElement, { blur = true, block = 'center' } = {}) {
  return reassertScroll(() => {
    getElement()?.scrollIntoView({ behavior: 'auto', block });
  }, { blur });
}

/**
 * Run `apply` now (post-paint) and re-assert it across the window in which the
 * iOS keyboard dismissal can clobber it. Shared by both exports above; see the
 * module comment for why a single call is not enough.
 */
function reassertScroll(apply, { blur }) {
  if (blur && document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }

  // The retry window has to counteract the keyboard WITHOUT ever fighting the
  // user. If they tap a field while we're still re-asserting — easy to do on a
  // step full of inputs — iOS scrolls that field into view and we would yank the
  // page back to the top from under them. So a focused editable element means
  // they've taken over and we stand down.
  const userIsTyping = () => {
    const el = document.activeElement;
    if (!(el instanceof HTMLElement)) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  };

  const run = () => {
    if (userIsTyping()) return;
    apply();
  };

  requestAnimationFrame(() => requestAnimationFrame(run));

  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  let lastHeight = vv ? vv.height : 0;

  // A viewport resize is only our signal when it GREW, meaning the keyboard
  // left. A shrinking viewport is the keyboard arriving — the user is starting to
  // type, and scrolling then would be actively hostile. The threshold ignores
  // browser-chrome noise (collapsing URL bar); a keyboard is hundreds of px.
  const onResize = () => {
    const height = vv.height;
    const grew = height > lastHeight + 80;
    lastHeight = height;
    if (grew) run();
  };
  vv?.addEventListener('resize', onResize);

  const timers = [60, 180, 360, 500].map((ms) => setTimeout(run, ms));

  const cleanup = () => {
    timers.forEach(clearTimeout);
    vv?.removeEventListener('resize', onResize);
  };
  timers.push(setTimeout(cleanup, 600));

  return cleanup;
}
